import { FormFieldProps } from '../Form.types';
import { STORABLE_FIELDS } from './knownVisitor';

export const ATTRIBUTION_QUERY_PARAMS = {
  utm_source: 'UTM_Sourcev1__c',
  utm_medium: 'UTM_Mediumv1__c',
  utm_campaign: 'UTM_Campaignv1__c',
  utm_content: 'UTM_Contentv1__c',
  utm_term: 'UTM_Termv1__c',
  gclid: 'GCLID__c',
} as const;

export const FORM_ATTRIBUTION_STORAGE_KEY = 'st_form_attribution';

/**
 * Options the visitor-facing dropdown offers. Salesforce picklist keys, never localized
 * (OPTION_LABELS holds the display text).
 *
 * Contentful's `productInterest` field is a superset of this (migration 16), so adding an option
 * here needs a migration but not the reverse.
 */
export const PRODUCT_INTEREST_OPTIONS = [
  'Mobile App Data',
  'Digital Advertising Data / Retail Media (Pathmatics)',
  'Audience Insights',
  'Web Insights',
  'Video Game Insights (PC / Console data)',
  'Playliner',
  'Not sure',
];

// The two form types named outside their own field list.
export const DEMO_PRODUCT_FORM_TYPE = 'demo_product';
export const UNSUBSCRIBE_FORM_TYPE = 'unsubscribe';

/**
 * Values of the SFDC `Marketing_Consent__c` picklist (#952) — exact picklist keys, never
 * localized. The picklist also has 'Unknown', which the site has no reason to send.
 */
export const MARKETING_CONSENT = {
  optedIn: 'Opted in',
  optedOut: 'Opted out',
  notSet: 'Not Set',
} as const;

export type MarketingConsent = (typeof MARKETING_CONSENT)[keyof typeof MARKETING_CONSENT];

/**
 * The marketing opt-in checkbox is not a FormFieldProps entry: its label is the Contentful
 * privacyPolicyDisclaimer rich text, so Form.tsx renders and validates it separately from
 * the field grid. Named here because the submit handler reads it from FormData by this name.
 */
export const MARKETING_OPT_IN_FIELD_NAME = 'marketingOptIn';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_REGEX = /^[\d\s+-]+$/;

// Field definitions are named so each form type can list the fields it wants explicitly.
// Composing by position (BASE_FIELDS[3], .slice(0, 2)) meant adding a base field silently
// reshaped every form type.

export const FIRST_NAME: FormFieldProps = {
  id: 'first-name',
  label: 'First Name',
  fieldName: 'firstName',
  inputType: 'text',
  autoComplete: 'given-name',
  isRequired: true,
  maxLength: 50,
};

export const LAST_NAME: FormFieldProps = {
  id: 'last-name',
  label: 'Last Name',
  fieldName: 'lastName',
  inputType: 'text',
  autoComplete: 'family-name',
  isRequired: true,
  maxLength: 50,
};

export const COMPANY: FormFieldProps = {
  id: 'company',
  label: 'Company',
  fieldName: 'company',
  inputType: 'text',
  autoComplete: 'organization',
  isRequired: true,
  maxLength: 100,
};

export const EMAIL: FormFieldProps = {
  id: 'email',
  label: 'Email',
  fieldName: 'email',
  inputType: 'email',
  autoComplete: 'email',
  isRequired: true,
  maxLength: 255,
};

// The unsubscribe form has to accept whichever address the subscriber signed up with,
// consumer domains included — blocking those would leave them unable to revoke consent.
// Every other form keeps EMAIL's business-only rule (#936 Field Specs).
export const EMAIL_ANY_DOMAIN: FormFieldProps = {
  ...EMAIL,
  allowConsumerEmail: true,
};

export const JOB_TITLE: FormFieldProps = {
  id: 'job-title',
  label: 'Job Title',
  fieldName: 'jobTitle',
  inputType: 'text',
  autoComplete: 'organization-title',
  isRequired: true,
  maxLength: 100,
};

export const MOBILE_PHONE: FormFieldProps = {
  id: 'phone',
  label: 'Mobile Phone',
  fieldName: 'phone',
  inputType: 'tel',
  autoComplete: 'tel',
  maxLength: 20,
};

export const PRODUCT_INTEREST: FormFieldProps = {
  id: 'product-interest',
  label: 'Product Interest',
  fieldName: 'productInterest',
  inputType: 'dropdown',
  isRequired: true,
  dropdownOptions: PRODUCT_INTEREST_OPTIONS,
  fullWidth: true,
};

export const TYPE_OF_REQUEST: FormFieldProps = {
  id: 'type-of-request',
  label: 'Type of Request',
  fieldName: 'typeOfRequest',
  inputType: 'dropdown',
  isRequired: true,
  dropdownOptions: ['Press', 'Research'],
};

// Reuses the Company field rather than a dedicated one, per #952 — only the label differs.
export const MEDIA_OUTLET: FormFieldProps = {
  id: 'media-outlet',
  label: 'Name of Media Outlet',
  fieldName: 'company',
  inputType: 'text',
  autoComplete: 'organization',
  isRequired: true,
  maxLength: 100,
  fullWidth: true,
};

export const QUESTION: FormFieldProps = {
  id: 'question',
  label: 'Question / Inquiry',
  fieldName: 'question',
  inputType: 'textarea',
  // The one free-text field. Question_Inquiry__c is an SFDC Long Text Area (32,768 default),
  // so this is a product cap rather than a schema one — generous for a press inquiry, and it
  // keeps a runaway paste from tripping the ingestion endpoint's 1 MB body limit.
  maxLength: 2000,
  fullWidth: true,
};

/** Fallback field list, and the shape shared by content_download and webinar forms. */
export const BASE_FIELDS: FormFieldProps[] = [FIRST_NAME, LAST_NAME, COMPANY, EMAIL];

const FORM_FIELD_API_NAMES = {
  firstName: 'FirstName',
  lastName: 'LastName',
  email: 'Email',
  company: 'Company',
  jobTitle: 'Title',
  phone: 'MobilePhone',
  productInterest: 'Products_of_Interest__c',
  typeOfRequest: 'Type_of_Request__c',
  question: 'Question_Inquiry__c',
} as const;

/** The downstream (SFDC) API name of every user-entered field — the payload type is built from it. */
export type FormFieldApiName = (typeof FORM_FIELD_API_NAMES)[keyof typeof FORM_FIELD_API_NAMES];

// Widened to a string index so it can be looked up by whatever fieldName a definition carries;
// Partial, because a fieldName with no mapping is a real miss the caller has to handle.
export const FORM_FIELD_TO_API_NAME: Partial<Record<string, FormFieldApiName>> = FORM_FIELD_API_NAMES;

// Maps this form's camelCase field names to the PascalCase keys used in the known-visitor cookie
export const FIELD_TO_COOKIE_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(FORM_FIELD_API_NAMES).filter(([, apiName]) =>
    (STORABLE_FIELDS as readonly string[]).includes(apiName),
  ),
);
